import { useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useLingui } from '@lingui/react';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { GenericError } from '@startuptools/ui/Error';
import { Modal } from '@startuptools/ui/Modal';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { useStSetPricePerShareMutation } from '../../../../../../graphql/react-operations';
import { ShareTransactionGroupsDocument } from '../../../Transactions.graphql';
import { TransactionsViewDocument } from '../../../transactions-view.graphql';
import { GqlCurrency, GqlShareTransaction } from '../../../../../../graphql/base-types.graphql';
import { Select } from '@startuptools/ui/Select';
import { Option } from '@startuptools/ui/Option';
import { currenciesShort } from '../../../../../../components/currencies';

interface FormInput {
  pricePerShare: string;
  currency: GqlCurrency;
}

export const EditPriceDialog = ({
  onClose,
  transaction,
  allowChangeCurrency,
}: {
  onClose: () => void;
  transaction: Pick<GqlShareTransaction, 'id' | 'currency' | 'pricePerShare'>;
  allowChangeCurrency: boolean;
}) => {
  const companyId = useCompanyId();
  const [setPricePerShareMutation, { loading }] = useStSetPricePerShareMutation({
    refetchQueries: [ShareTransactionGroupsDocument, TransactionsViewDocument],
  });
  const { control, handleSubmit } = useForm<FormInput>({
    defaultValues: { currency: transaction.currency, pricePerShare: transaction.pricePerShare ?? '' },
  });
  const { i18n } = useLingui();
  const [showError, setShowError] = useState<boolean>(false);

  const onSubmit: SubmitHandler<FormInput> = async ({ currency, pricePerShare }) => {
    if (showError) setShowError(false);
    try {
      await setPricePerShareMutation({
        variables: {
          input: {
            id: transaction.id,
            pricePerShare: pricePerShare.length > 0 ? pricePerShare : null,
            currency,
          },
          companyId,
        },
      });
      onClose();
    } catch {
      setShowError(true);
    }
  };

  return (
    <Modal open onClose={onClose}>
      <ModalDialog>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={3}>
            <Typography level="h4">
              <Trans id="Set price per Share" />
            </Typography>
            <Controller
              control={control}
              name="pricePerShare"
              render={({ field }) => (
                <FormControl>
                  <FormLabel>
                    <Trans id="Price per Share" />
                  </FormLabel>
                  <NumericFormatInput
                    {...field}
                    fullWidth
                    variant="outlined"
                    lang={i18n.locale}
                    endDecorator={
                      allowChangeCurrency ? (
                        <Controller
                          control={control}
                          name="currency"
                          render={({ field: { onChange, ...field } }) => (
                            <Select {...field} onChange={(_, v) => onChange(v)} variant="plain" color="neutral">
                              {currenciesShort.map(c => (
                                <Option key={c.code} value={c.code}>
                                  {c.code}
                                </Option>
                              ))}
                            </Select>
                          )}
                        />
                      ) : (
                        transaction.currency
                      )
                    }
                    disabled={loading}
                  />
                </FormControl>
              )}
            />
            {showError && <GenericError />}
            <Stack direction="row" gap={1} justifyContent="end" alignItems="center">
              <Button type="button" onClick={onClose} variant="plain" disabled={loading}>
                <Trans id="Cancel" />
              </Button>
              <Button type="submit" loading={loading}>
                <Trans id="Save" />
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
};
